import React from 'react'
import { SupplementalMedia } from 'Components/supplementalMedia'
import { TourGuide } from 'Components/tourGuide'
import { MainMedia } from 'Components/tourStop'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { useSelector } from 'react-redux'
import { useGetLanguagesQuery, Labels } from 'Services/institution'

interface TourStopProps {
  stopData?: {
    stopid: number
    descriptionid: number
    description_id: number
    title: string
    stop_title: string
    main_media_id: number
    main_media_type: string
    media_id?: number
  }
  personalized?: boolean
  labels?: Labels
}

export const TourStopContainer = ({
  stopData: {
    stopid: stopId,
    descriptionid: descriptionId,
    description_id: personalizedDescriptionId,
    title,
    stop_title: personalizedTitle,
    main_media_id: mainMediaId,
    main_media_type: mainMediaType,
    media_id: pointMediaId,
  },
  personalized,
  labels,
}: TourStopProps) => {
  const { locId, instId } = useSelector((state: any) => state.location)
  const { data: allLangs } = useGetLanguagesQuery({
    instId,
    locId,
  })

  const label = (labels && labels[stopId] && labels[stopId]?.join(', ')) || null

  let ariaLabel = title || personalizedTitle
  if (label) {
    ariaLabel += `, the destination includes ${label}. `
  }
  if (personalized) {
    ariaLabel += ' For You'
  }

  return (
    <>
      <Accordion slotProps={{ transition: { unmountOnExit: true } }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`stop-${stopId}-content`}
          id={`stop-${stopId}-header`}
          aria-label={ariaLabel}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'baseline',
                gap: 1,
              }}
            >
              <Typography
                component='span'
                role='heading'
                aria-level={2}
                sx={{ fontWeight: 700, pr: 0.5 }}
              >
                {title || personalizedTitle}
              </Typography>
              {label && (
                <Typography
                  component='span'
                  sx={{ fontSize: 14, color: '#767676', ml: 2.5 }}
                >
                  {label}
                </Typography>
              )}
            </Box>
            {personalized && (
              <Box
                data-testid='personalization-indicator'
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  mr: '18px',
                }}
              >
                <AutoAwesomeIcon
                  data-testid='sparkle-icon'
                  sx={{
                    color: 'gold',
                    paddingRight: '5px',
                    paddingLeft: '5px',
                  }}
                />{' '}
                <span data-testid='for-you-tag'>For You</span>
              </Box>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails id={`stop-${stopId}-content`}>
          <MainMedia
            id={mainMediaId}
            type={mainMediaType}
            pointMediaId={pointMediaId}
            stopId={stopId}
            stopTitle={title || personalizedTitle}
          />
          {allLangs?.resources.map((lang) => (
            <TourGuide
              language={lang.language}
              descriptionId={descriptionId || personalizedDescriptionId}
            />
          ))}
          <Box component='div' sx={{ mb: 1 }}></Box>
          <SupplementalMedia stopId={stopId} mainMediaId={mainMediaId} />
        </AccordionDetails>
      </Accordion>
    </>
  )
}
